import { useEffect, useRef, useState } from "react";
import { Instagram } from "lucide-react";

const INSTAGRAM_URL = "https://www.instagram.com/blackswan_productions/";

const BEHOLD_FEED_ID =
  import.meta.env.VITE_BEHOLD_FEED_ID ?? "XIaOjreGB7SeAAmEg05d";

type BeholdSize = { mediaUrl?: string };

type BeholdPost = {
  id: string;
  permalink?: string;
  mediaType?: string;
  mediaUrl?: string;
  thumbnailUrl?: string;
  prunedCaption?: string;
  caption?: string;
  sizes?: { small?: BeholdSize; medium?: BeholdSize; large?: BeholdSize };
};

function imageFor(post: BeholdPost): string | undefined {
  return (
    post.sizes?.medium?.mediaUrl ??
    post.sizes?.large?.mediaUrl ??
    post.sizes?.small?.mediaUrl ??
    post.thumbnailUrl ??
    (post.mediaType === "VIDEO" ? undefined : post.mediaUrl)
  );
}

export function InstagramFeed() {
  const [posts, setPosts] = useState<BeholdPost[]>([]);
  const [ready, setReady] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  /* Defer the feed request until the visitor scrolls near this section, so
     it stays out of the critical request chain on initial page load. */
  useEffect(() => {
    if (ready) return;
    const el = sentinelRef.current;
    if (!el) return;

    if (typeof IntersectionObserver === "undefined") {
      setReady(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setReady(true);
          observer.disconnect();
        }
      },
      { rootMargin: "600px 0px" },
    );
    observer.observe(el);

    return () => observer.disconnect();
  }, [ready]);

  useEffect(() => {
    if (!ready || !BEHOLD_FEED_ID) return;

    let active = true;
    fetch(`https://feeds.behold.so/${BEHOLD_FEED_ID}`)
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((data) => {
        if (!active) return;
        const raw: BeholdPost[] = Array.isArray(data) ? data : (data?.posts ?? []);
        setPosts(raw.filter((p) => imageFor(p)));
      })
      .catch(() => {
        if (active) setPosts([]);
      });

    return () => {
      active = false;
    };
  }, [ready]);

  if (posts.length === 0) {
    return <div ref={sentinelRef} aria-hidden="true" />;
  }

  const MIN_TILES = 8;
  const repeats = Math.max(1, Math.ceil(MIN_TILES / posts.length));
  const base = Array.from({ length: repeats }, () => posts).flat();
  const track = [...base, ...base];

  return (
    <section className="py-20 border-t border-white/5 overflow-hidden bg-black relative">
      <div className="container mx-auto px-6 mb-12">
        <a
          href={INSTAGRAM_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="group flex items-center justify-center gap-3 text-gray-400 hover:text-white transition-colors duration-300"
        >
          <Instagram className="w-5 h-5 text-primary" />
          <span className="uppercase tracking-[0.25em] text-xs md:text-sm font-semibold">
            Follow us on Instagram
          </span>
          <span className="text-gray-400 group-hover:text-primary transition-colors duration-300">
            @blackswan_productions
          </span>
        </a>
      </div>

      <div
        className="relative flex w-full"
        style={{
          maskImage:
            "linear-gradient(to right, transparent, #000 8%, #000 92%, transparent)",
          WebkitMaskImage:
            "linear-gradient(to right, transparent, #000 8%, #000 92%, transparent)",
        }}
      >
        <div
          className="flex shrink-0 items-center animate-marquee"
          style={{ "--marquee-duration": "70s" } as React.CSSProperties}
        >
          {track.map((post, idx) => {
            const src = imageFor(post);
            return (
              <a
                key={`${post.id}-${idx}`}
                href={post.permalink ?? INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={post.prunedCaption ?? "View Instagram post"}
                className="group relative flex-shrink-0 h-48 w-48 md:h-56 md:w-56 mr-4 md:mr-6 rounded-xl overflow-hidden border border-white/5 bg-white/[0.02]"
              >
                <img
                  loading="lazy"
                  decoding="async"
                  src={src}
                  alt={post.prunedCaption ?? "Black Swan Productions on Instagram"}
                  className="h-full w-full object-cover opacity-80 transition-all duration-500 group-hover:opacity-100 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-500 flex items-center justify-center">
                  <Instagram className="w-7 h-7 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}
